import React, { ComponentType, useContext } from 'react'
import { Button } from '@/components/ui/button'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridItemChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TransactionNoteDto } from 'api/Transaction/Types/TransactionNoteDto'
import { Loading } from 'components/Loading'
import { NotesCommandCell } from 'components/Note/NotesCommandCell'
import { useNoteState } from 'components/Note/noteState'
import { NoteValue } from 'components/Note/types'
import { formatISO } from 'date-fns'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { hasPermission } from 'utils/permissionsValidator'

import { transactionNotesColumns } from '../../../../Transactions/transactionNotesColumns'

interface ClientDetailsPaymentsNotesProps {
  notes: TransactionNoteDto[]
  permissions: PermissionDto[]
  onItemChange(event: GridItemChangeEvent): void
  onAddNew(): void
  actionCell: ComponentType<GridCellProps>
}

const ClientDetailsPaymentsNotesUI: React.FC<ClientDetailsPaymentsNotesProps> = (props) => {
  const { notes, permissions, actionCell, onItemChange, onAddNew } = props

  return (
    <Grid resizable data={notes} onItemChange={onItemChange} editField='inEdit'>
      <GridToolbar>
        <Button
          onClick={onAddNew}
          size='xs'
          disabled={!hasPermission(permissions, PermissionsEnum.CanAddClientNotes)}
        >
          Add new
        </Button>
      </GridToolbar>
      {transactionNotesColumns
        .filter((c) => c.show !== false)
        .map((column, idx) => (
          <Column key={idx + 2} {...column} />
        ))}
      <Column cell={actionCell} width='150px' />
    </Grid>
  )
}

interface OuterProps {
  data: ClientDetailedDto
}

export const ClientDetailsPaymentsNotes: React.FC<OuterProps> = (props) => {
  const { data } = props

  const walletsApiClent = useApiClient(WalletsApiClient)
  const [auth] = useContext(AuthSessionContext)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(() => {
    return walletsApiClent.getTransactionNotesByClientId(data.id)
  }, [])
  const { data: notesData = [], isLoading } = useFetchOne(fetchData)

  const permissions = auth?.permissions || []

  const handleAdd = async (dataItem: TransactionNoteDto) => {
    if (data?.id) {
      await walletsApiClent.addTransactionNote({
        clientId: data.id,
        description: dataItem.description,
        expiryDate: dataItem.expiryDate
          ? formatISO(new Date(dataItem.expiryDate.toString()), { representation: 'date' })
          : null,
        masterTransactionId: null,
      })
    }
  }

  const handleUpdate = async (dataItem: TransactionNoteDto) => {
    if (data?.id) {
      await walletsApiClent.updateTransactionNote({
        id: dataItem.id as string,
        description: dataItem.description,
        expiryDate: dataItem.expiryDate
          ? formatISO(new Date(dataItem.expiryDate.toString()), { representation: 'date' })
          : null,
      })
    }
  }

  const handleRemove = async (dataItem: TransactionNoteDto) => {
    if (dataItem?.id) {
      await walletsApiClent.removeTransactionNoteById(dataItem.id)
    }
  }

  const handleAddNew = () => {
    addNew({
      id: null,
      transaction: null,
      client: null,
      Discontinued: false,
      description: null,
      expiryDate: null,
      updatedBy: null,
    })
  }

  const handleEdit = (note: NoteValue<TransactionNoteDto>) => {
    setEditNote(note)
  }

  const { notes, setNotes, setEditNote, addNew, ...noteState } = useNoteState({
    onAdd: handleAdd,
    onEdit: handleEdit,
    onRemove: handleRemove,
    onUpdate: handleUpdate,
    forceRefresh,
    notes: notesData,
  })

  return (
    <Loading isLoading={isLoading}>
      <ClientDetailsPaymentsNotesUI
        notes={notes}
        permissions={permissions}
        onAddNew={handleAddNew}
        onItemChange={noteState.itemChange}
        actionCell={NotesCommandCell({
          ...noteState,
          CanEdit: hasPermission(permissions, PermissionsEnum.CanEditPaymentNotes),
          CanDelete: hasPermission(permissions, PermissionsEnum.CanDeletePaymentNotes),
        })}
      />
    </Loading>
  )
}
